import {FC, useEffect, useState} from 'react'
import {useHistory, useParams} from 'react-router-dom'
import {ExpertiseCheckout, getCheckout, payEngagement} from '../../services/expertiseAdminService'
import {showErrorAlert} from '../../helpers/alert'
import {handleApiError} from '../../utils/handleApiError'
import {ExpertiseOfferCard} from './components/ExpertiseOfferCard'
import {PaymentMethodModel} from '../paymentMethod/components/PaymentMethodModel'
import {getMyPaymentMethods} from '../../services/financialService'

/**
 * Resumen previo al pago (Figma 4457:77327). Solo la ve el comprador.
 *
 * El desglose NO se calcula aqui: llega ya resuelto del backend, para que la cifra que se muestra
 * y la que se va a cobrar salgan del mismo lugar.
 *
 * El cobro en si (Stripe) es el paso siguiente y todavia no esta definido — el boton queda inerte
 * a proposito en vez de simular un pago que no ocurre.
 */
const money = (amount?: string | null, currency?: string) => {
  if (amount === null || amount === undefined) return '—'
  const n = Number(amount)
  if (isNaN(n)) return String(amount)
  const abs = Math.abs(n)
  const body =
    abs % 1 === 0 ? abs.toLocaleString('en-US') : abs.toLocaleString('en-US', {minimumFractionDigits: 2})
  const sign = n < 0 ? '-' : ''
  const symbol = currency && currency.toUpperCase() !== 'USD' ? `${currency.toUpperCase()} ` : '$'
  return `${sign}${symbol}${body}`
}

const ExpertiseCheckoutPage: FC = () => {
  const {requestId} = useParams<{requestId: string}>()
  const history = useHistory()
  const [data, setData] = useState<ExpertiseCheckout | null>(null)
  const [loading, setLoading] = useState(true)
  const [accepted, setAccepted] = useState(false)
  const [showAddCard, setShowAddCard] = useState(false)
  const [checking, setChecking] = useState(false)

  useEffect(() => {
    let alive = true
    setLoading(true)
    getCheckout(Number(requestId))
      .then((d) => alive && setData(d))
      .catch((error) => alive && handleApiError(error))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [requestId])

  /**
   * Cobra el trato contra el servicio de pagos.
   *
   * El monto no viaja: lo toma del `agreed_amount` del Engagement. Al cobrar, ese servicio marca la
   * cuota como pagada y mueve el trato a PAID, que es lo que completa el paso 4 del timeline y
   * habilita el cierre en el paso 5.
   */
  const pay = async (engagementId: number) => {
    try {
      const {status, data: result} = await payEngagement(engagementId)

      // 202: el banco pide 3D Secure. Aqui hace falta confirmar el intent con `clientSecret` desde
      // Stripe.js; sin ese paso el cobro queda a medias, asi que se avisa en vez de darlo por bueno.
      if (status === 202 || result.requiresAction) {
        showErrorAlert({
          title: 'Additional verification required',
          message: result.message || 'Your bank requires 3D Secure authentication for this payment.',
        })
        return
      }

      // Se sale del checkout a la pantalla de gracias en vez de quedarse aqui: la tarjeta del
      // resumen ya no tiene nada que ofrecer una vez cobrada.
      history.push('/expertise/payment-success')
    } catch (error: any) {
      handleApiError(error)
    }
  }

  const handleSubmit = async () => {
    if (!data || checking) return
    try {
      setChecking(true)
      const methods = await getMyPaymentMethods({offset: 0, limit: 1})
      const list = Array.isArray(methods) ? methods : methods?.results ?? []

      // Sin tarjeta guardada no se puede cobrar, asi que primero se pide una. El flujo NO se corta:
      // al guardarla continua solo, en `handleCardAdded`.
      if (!list.length) {
        setShowAddCard(true)
        return
      }
      await pay(data.engagementId)
    } catch (error: any) {
      handleApiError(error)
    } finally {
      setChecking(false)
    }
  }

  /** La tarjeta quedo guardada: se cierra el modal y sigue el mismo camino que si ya la tuviera. */
  const handleCardAdded = async () => {
    setShowAddCard(false)
    if (data) await pay(data.engagementId)
  }

  if (loading) return <div className='members-page__no-results'>Loading...</div>
  if (!data) return <div className='members-page__no-results'>Checkout not available</div>

  const adjustment = Number(data.adjustment ?? 0)
  const paid = data.paymentStatus === 'paid'

  return (
    <div className='expertise-checkout'>
      <div className='expertise-checkout__main'>
        <span className='expertise-checkout__eyebrow'>Expertise</span>
        <h1 className='expertise-checkout__title'>{data.expert.companyName}</h1>

        {data.service && <ExpertiseOfferCard service={data.service} variant='static' />}
      </div>

      <aside className='expertise-checkout__aside'>
        <div className='expertise-checkout__summary nb3-scope'>
          <div className='expertise-checkout__amounts'>
            <div className='expertise-checkout__line'>
              <span>
                Rate x {data.hours} {data.hours === 1 ? 'hour' : 'hours'}
              </span>
              <span>{money(data.subtotal, data.currency)}</span>
            </div>

            {/* El ajuste solo aparece si el experto movio el monto. En cero seria un renglon que
                no dice nada. */}
            {adjustment !== 0 && (
              <div className='expertise-checkout__line'>
                <span>{adjustment > 0 ? 'Adjustment by Expert' : 'Discount by Expert'}</span>
                <span>{money(data.adjustment, data.currency)}</span>
              </div>
            )}

            <div className='expertise-checkout__divider' />

            <div className='expertise-checkout__total'>
              <span className='expertise-checkout__total-label'>Total fee</span>
              <span className='expertise-checkout__total-value'>{money(data.total, data.currency)}</span>
            </div>
          </div>

          <p className='expertise-checkout__note'>
            This is the final amount agreed with the Expert. If the session does not take place, the
            refund follows the cancellation terms of the deal.
          </p>

          <div className='expertise-checkout__divider' />

          {/* Checkbox compartido de la app, para no tener dos estilos de casilla.
              Va SOLO con `nb3-check`, sin `nbq-check`: las dos clases dibujan su propia caja y su
              propia palomita sobre los mismos pseudo-elementos, y juntas se pisan. En el registro
              conviven porque `.nb-step3` anula los pseudos de la global; aqui no existe ese scope.
              El ancestro `nb3-scope` va en el contenedor de la tarjeta. */}
          <label className='expertise-checkout__terms'>
            <span className='nb3-check'>
              <input
                className='form-check-input'
                type='checkbox'
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
              />
            </span>
            <span className='expertise-checkout__terms-text'>
              I agree to adhere to the platform&apos;s code of conduct and privacy guidelines.
            </span>
          </label>

          <button
            type='button'
            className='expertise-checkout__submit'
            disabled={!accepted || paid || checking}
            onClick={handleSubmit}
          >
            <span>{paid ? 'Already paid' : checking ? 'Processing...' : 'Submit and pay'}</span>
            <svg width='13' height='4' viewBox='0 0 13.3502 3.88356' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
              <path d='M8.5 0.379406L12 3.38356H0' stroke='currentColor' />
            </svg>
          </button>
        </div>

        {/* Fuera de la tarjeta: el diseno la cierra en el boton. Se conserva porque abandonar el
            checkout sin esto deja al comprador sin vuelta al hilo. */}
        <button
          type='button'
          className='expertise-checkout__back'
          onClick={() => history.push('/messages')}
        >
          Back to messages
        </button>
      </aside>

      {/* El mismo modal de /user/payment/method: alta de tarjeta con Stripe. Se reusa en vez de
          duplicarlo para que la captura de tarjeta viva en un solo sitio. */}
      <PaymentMethodModel
        show={showAddCard}
        onClose={() => setShowAddCard(false)}
        onSubmit={handleCardAdded}
      />
    </div>
  )
}

export {ExpertiseCheckoutPage}
export default ExpertiseCheckoutPage
